/* Reveal behaviour for the ported pages.
 *
 * clayit-web does this with two React hooks. Neither needs React:
 *
 *   useReveal      adds .is-in to a card once it enters view, one shot
 *   PressedMark    adds .is-in to the mark at 25% visible, which starts the seven-stroke draw
 *
 * Both are one-shot on purpose. A reveal that replays every time you scroll past is a
 * distraction rather than an entrance, and it is the single most common way this pattern
 * gets built wrong.
 *
 * With scripting off, nothing gets .is-in. The copied CSS renders those elements finished
 * rather than hidden, so the page is complete without this file. That is the reason the
 * reveal is additive here instead of an opacity:0 that JavaScript has to rescue.
 */

(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) {
    /* No observer: show everything immediately rather than leaving it mid-animation. */
    var all = document.querySelectorAll('[data-reveal], [data-pressed-mark]');
    for (var i = 0; i < all.length; i++) all[i].classList.add('is-in');
    return;
  }

  var once = function (nodes, options) {
    if (!nodes.length) return;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        entries[i].target.classList.add('is-in');
        io.unobserve(entries[i].target);
      }
    }, options);
    for (var j = 0; j < nodes.length; j++) io.observe(nodes[j]);
  };

  /* Cards: the same -12% bottom margin clayit-web uses, so a card commits just before it is
     fully on screen rather than after. */
  once(document.querySelectorAll('[data-reveal]'), { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });

  /* The mark: a quarter visible, matching PressedMark.jsx. */
  once(document.querySelectorAll('[data-pressed-mark]'), { threshold: 0.25 });
})();

/* ---------------------------------------------------------------------------
   Contact sheet: the archive zooms, fades, and resolves into one frame.

   Ported from clayit-web ContactSheet.jsx + useProgress.js. Every constant below is that
   file's, not a re-tuned approximation:

     mosaic scale   1 -> 1.75   over p 0.00 .. 0.58
     mosaic fade    1 -> 0      over p 0.30 .. 0.52
     resolve in     0 -> 1      over p 0.50 .. 0.72   (and scale 1.1 -> 1)
     title out      1 -> 0      over p 0.02 .. 0.30
     caption in     0 -> 1      over p 0.74 .. 0.92

   The zoom is capped at 1.75 for a reason worth keeping: a tile is 100vw/8 at rest, so past
   that it upsamples, and `sizes` in the markup is written to match the cap exactly. Raise one
   without the other and the browser either serves blurry tiles or pulls the 1200px file forty
   times, which is about 2 MB.

   Progress is read inside requestAnimationFrame so the scroll listener itself never does
   layout work.

   Under reduced motion the three animated overlays are REMOVED, not just left at zero. React
   does not render them at all in that state, and an element sitting at opacity 0 over a link
   is not the same page as one that was never there.
--------------------------------------------------------------------------- */

(function () {
  'use strict';

  var sheet = document.querySelector('[data-contact-sheet]');
  if (!sheet) return;

  var grid = sheet.querySelector('[data-cs-grid]');
  var resolve = sheet.querySelector('[data-cs-resolve]');
  var veil = sheet.querySelector('[data-cs-veil]');
  var capveil = sheet.querySelector('[data-cs-capveil]');
  var title = sheet.querySelector('[data-cs-title]');
  var cap = sheet.querySelector('[data-cs-cap]');

  var reduced = false;
  try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  if (reduced) {
    sheet.classList.add('cs-static');
    [resolve, capveil, cap].forEach(function (el) { if (el && el.parentNode) el.parentNode.removeChild(el); });
    if (veil) veil.style.opacity = '1';
    if (title) { title.style.opacity = '1'; title.style.transform = 'translateY(0px)'; }
    return;
  }

  var clamp01 = function (v) { return v < 0 ? 0 : v > 1 ? 1 : v; };
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var range = function (v, i0, i1) { return clamp01((v - i0) / (i1 - i0)); };

  var frame = 0;

  var measure = function () {
    frame = 0;
    var r = sheet.getBoundingClientRect();
    var travel = r.height - window.innerHeight;
    var p = travel <= 0 ? (r.top <= 0 ? 1 : 0) : clamp01(-r.top / travel);

    var mosaicScale = lerp(1, 1.75, range(p, 0, 0.58));
    var mosaicFade = 1 - range(p, 0.3, 0.52);
    var resolveIn = range(p, 0.5, 0.72);
    var resolveScale = lerp(1.1, 1, resolveIn);
    var titleOut = 1 - range(p, 0.02, 0.3);
    var capIn = range(p, 0.74, 0.92);

    grid.style.transform = 'scale(' + mosaicScale.toFixed(4) + ')';
    grid.style.opacity = mosaicFade.toFixed(3);
    resolve.style.opacity = resolveIn.toFixed(3);
    resolve.style.transform = 'scale(' + resolveScale.toFixed(4) + ')';
    veil.style.opacity = (titleOut * 0.9 + 0.1).toFixed(3);
    capveil.style.opacity = capIn.toFixed(3);
    title.style.opacity = titleOut.toFixed(3);
    title.style.transform = 'translateY(' + (-18 * (1 - titleOut)).toFixed(1) + 'px)';
    cap.style.opacity = capIn.toFixed(3);
    cap.style.pointerEvents = capIn > 0.6 ? 'auto' : 'none';
  };

  var onScroll = function () {
    if (frame) return;
    frame = requestAnimationFrame(measure);
  };

  measure();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
})();

/* ---------------------------------------------------------------------------
   Studio plate drift.

   A slow counter-parallax on the three discipline frames. Its job is to make the picture
   feel set INTO the page rather than laid on top of it, which is the difference between a
   printed plate and a stock content block. That is the whole stated intent; if it were doing
   anything more than that it would be decoration and should be cut.

   Constraints it holds to:
     transform only, never a layout property, so it cannot cause a reflow or a layout shift
     bounded at 4% of the frame's own height per side, which is the headroom studio.css
       already reserved with scale(1.09), so an edge is never exposed
     one rAF per scroll burst, and only for frames actually in the viewport
     off entirely under reduced motion, where studio.css also drops the scale

   Early-returns on every other page in the theme, which is why it can live in this shared
   file rather than costing a second request.
   --------------------------------------------------------------------------- */

(function () {
  'use strict';

  var frames = document.querySelectorAll('[data-sd-drift]');
  if (!frames.length) return;

  var reduce = false;
  try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
  if (reduce) return;

  var ticking = false;

  var apply = function () {
    ticking = false;
    var vh = window.innerHeight;

    for (var i = 0; i < frames.length; i++) {
      var frame = frames[i];
      var img = frame.firstElementChild;
      if (!img) continue;

      var box = frame.getBoundingClientRect();
      if (box.bottom < 0 || box.top > vh) continue;

      /* +1 when the frame's centre is at the top of the viewport, -1 at the bottom, 0 when
         it is centred. Dividing by half the viewport PLUS half the frame keeps the range
         honest for a frame taller than the viewport. */
      var centre = box.top + box.height / 2;
      var t = (vh / 2 - centre) / (vh / 2 + box.height / 2);
      if (t < -1) t = -1;
      if (t > 1) t = 1;

      var max = box.height * 0.04;
      img.style.transform = 'translate3d(0,' + (t * max).toFixed(2) + 'px,0) scale(1.09)';
    }
  };

  var onScroll = function () {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  apply();
})();
